'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { HelpCircle } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
  NoticeBanner,
} from '@/shared/ui';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Typography } from '@/shared/ui/shadcn/typography';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { useProject } from '@/features/project/hooks/use-projects';
import { useProjectGoalsGuide } from '@/features/project/hooks/use-goals-guide';
import { GoalsBriefGuide } from '@/features/project/ui/GoalsBriefGuide';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import {
  FacadeAreaField,
  parseFacadeArea,
} from '@/features/project/ui/FacadeAreaField';

interface ProjectGoalsSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * NestedSheet with the project goals editor (goals + construction region +
 * facade area). Shared by the render-settings goals row and the estimate
 * editor's facade-area link — both surfaces open the SAME editor instead of
 * bouncing the user out to the standalone goals page. Mirrors Apple's
 * `ProjectGoalsView` sheet and Android's `ProjectGoalsPanelContent` bottom
 * sheet. Save plumbing (debounce, mutex, retry) lives in
 * `goalsAutosaveCoordinator` so callers can `flushAndAwait` any pending PATCH.
 *
 * The editor is a plain local text buffer: it seeds once from the project and
 * pushes debounced PATCHes. It deliberately does NOT pull server updates back
 * into the field while open — doing so let a refetch overwrite what the user
 * was typing, which the autosave then persisted as empty (the goals-wipe).
 * Freshness comes on next open.
 */
export function ProjectGoalsSheet({
  projectId,
  open,
  onOpenChange,
}: ProjectGoalsSheetProps) {
  const t = useTranslations('projectGoals');
  // Shared "Situation and goals" copy (notice + region labels) lives in the
  // `project` namespace so all goals surfaces show identical text.
  const tProject = useTranslations('project');
  // Server-driven goals copy ("How to write a great brief" guide + references hint).
  const { data: goalsGuide } = useProjectGoalsGuide();

  const { data: projectResponse } = useProject(projectId);
  const project = projectResponse?.data;
  const serverGoals = project?.attributes.goals ?? '';
  const serverRegion = project?.attributes.construction_region ?? '';
  const serverArea =
    project?.attributes.facade_area != null
      ? String(project.attributes.facade_area)
      : '';

  const [goals, setGoals] = useState(serverGoals);
  const [region, setRegion] = useState(serverRegion);
  const [area, setArea] = useState(serverArea);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Only an actually-edited area rides along in the PATCH — sending the field
  // (even unchanged) marks it user-owned and clears the "estimated" flag.
  const areaChanged = area.trim() !== serverArea;
  const pendingArea = areaChanged ? parseFacadeArea(area) : undefined;

  // Seed the editor once when the project first loads. After this the field is
  // a local buffer — later server updates are intentionally ignored so a
  // refetch can't trample what the user is typing.
  useEffect(() => {
    if (!project || hasInitialized) return;
    setGoals(serverGoals);
    setRegion(serverRegion);
    setArea(serverArea);
    setHasInitialized(true);
  }, [project, serverGoals, serverRegion, serverArea, hasInitialized]);

  const flushSave = () => {
    if (
      goals.trim() === serverGoals &&
      region.trim() === serverRegion &&
      !areaChanged
    )
      return;
    goalsAutosaveCoordinator.flushNow(projectId, goals, region, pendingArea);
  };

  // Hand each edit to the coordinator (debounced PATCH). The coordinator
  // owns the timer + in-flight Promise; this effect only forwards.
  useEffect(() => {
    if (!hasInitialized) return;
    if (goals === serverGoals && region === serverRegion && !areaChanged)
      return;
    goalsAutosaveCoordinator.schedule(projectId, goals, region, pendingArea);
  }, [
    projectId,
    goals,
    region,
    hasInitialized,
    serverGoals,
    serverRegion,
    areaChanged,
    pendingArea,
  ]);

  return (
    <NestedSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) flushSave();
        onOpenChange(next);
      }}
    >
      <NestedSheetContent>
        <NestedSheetHeader>
          <NestedSheetTitle>{t('label')}</NestedSheetTitle>
        </NestedSheetHeader>

        <NestedSheetBody className="px-4">
          <div className="space-y-6 py-2">
            {/* Native-language notice and the "How to write a great brief"
                guide sit above the editor (no preamble copy) so the guidance
                is the first thing the user reads — matches the standalone
                goals page and the Apple/Android surfaces. */}
            <NoticeBanner
              variant="info"
              message={tProject('goalsLanguageNoticeMessage')}
            />

            {goalsGuide && <GoalsBriefGuide guide={goalsGuide.guide} />}

            <div className="space-y-2">
              <Label htmlFor="render-goals" className="sr-only">
                {t('label')}
              </Label>
              <Textarea
                id="render-goals"
                value={goals}
                onChange={(e) => setGoals(e.target.value)}
                onBlur={flushSave}
                placeholder={tProject('projectGoalsPlaceholder')}
                rows={6}
                maxLength={10000}
                className="min-h-[200px] resize-none"
              />
            </div>

            {/* Construction Region field with label + help popover */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="render-region">
                  {tProject('constructionRegion')}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="start">
                    <p className="text-form-help text-muted-foreground">
                      {tProject('constructionRegionHelpText')}
                    </p>
                  </PopoverContent>
                </Popover>
              </div>
              <Input
                id="render-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                onBlur={flushSave}
                placeholder={tProject('constructionRegionPlaceholder')}
                maxLength={255}
              />
            </div>

            {/* Facade area — optional; estimates use it as the
                authoritative building area instead of re-guessing. */}
            <FacadeAreaField
              id="render-facade-area"
              value={area}
              unit={project?.attributes.facade_area_unit ?? 'm2'}
              isEstimated={
                !!project?.attributes.facade_area_is_estimated && !areaChanged
              }
              onChange={setArea}
              onBlur={flushSave}
            />

            {/* References upload hint — kept at the bottom so the most
                important guidance stays above the fold. */}
            {goalsGuide && (
              <Typography variant="muted" className="text-form-help">
                {goalsGuide.references_hint}{' '}
                <Link
                  href={`/project/${projectId}/references`}
                  className="text-primary hover:underline"
                >
                  {goalsGuide.references_link}
                </Link>
                .
              </Typography>
            )}
          </div>
        </NestedSheetBody>
      </NestedSheetContent>
    </NestedSheet>
  );
}
